import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiFileText,FiUsers,FiShield,FiAlertTriangle,FiMail,FiCalendar,FiCheckCircle,FiXCircle}=FiIcons;

const Terms=()=> {
const [headerRef,headerInView]=useInView({triggerOnce: true,threshold: 0.1});
const [contentRef,contentInView]=useInView({triggerOnce: true,threshold: 0.1});

const sections=[
{
icon: FiUsers,
title: 'Acceptance of Terms',
content: [
{
subtitle: 'Agreement to Terms',
text: 'By accessing or using Reliabotix AI Solutions services,you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any of these terms,you are prohibited from using our services.'
},
{
subtitle: 'Eligibility',
text: 'You must be at least 18 years old and have the legal authority to enter into these terms. If you are using our services on behalf of an organization,you represent that you have the authority to bind that organization to these terms.'
},
{
subtitle: 'Modifications',
text: 'We reserve the right to modify these terms at any time. We will notify users of material changes via email or through our platform. Continued use of our services after changes constitutes acceptance of the new terms.'
}
]
},
{
icon: FiFileText,
title: 'Description of Services',
content: [
{
subtitle: 'AI Solutions',
text: 'Reliabotix provides artificial intelligence solutions including but not limited to automated workflows,predictive analytics,conversational AI,and custom AI applications. Our services are designed to enhance business operations and decision-making processes.'
},
{
subtitle: 'Service Availability',
text: 'We strive to maintain high service availability but do not guarantee uninterrupted access. Services may be temporarily unavailable due to maintenance,updates,or circumstances beyond our control.'
},
{
subtitle: 'Service Modifications',
text: 'We may modify,suspend,or discontinue any aspect of our services at any time. We will provide reasonable notice of significant changes that may affect your use of our services.'
}
]
},
{
icon: FiShield,
title: 'User Responsibilities',
content: [
{
subtitle: 'Account Security',
text: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized access or security breaches.'
},
{
subtitle: 'Acceptable Use',
text: 'You agree to use our services only for lawful purposes and in accordance with these terms. You may not use our services to transmit harmful content,violate intellectual property rights,or engage in any illegal activities.'
},
{
subtitle: 'Data Accuracy',
text: 'You are responsible for the accuracy and legality of any data you provide to our services. You warrant that you have the necessary rights and permissions for any data you upload or process through our platform.'
}
]
},
{
icon: FiCheckCircle,
title: 'Intellectual Property Rights',
content: [
{
subtitle: 'Our Intellectual Property',
text: 'All content,features,and functionality of our services,including but not limited to software,text,graphics,logos,and trademarks,are owned by Reliabotix or our licensors and are protected by intellectual property laws.'
},
{
subtitle: 'User Content',
text: 'You retain ownership of any content you provide to our services. By using our services,you grant us a limited,non-exclusive license to use your content solely for the purpose of providing our services to you.'
},
{
subtitle: 'Feedback',
text: 'Any feedback,suggestions,or ideas you provide to us may be used by Reliabotix without compensation or attribution. This helps us improve our services for all users.'
}
]
},
{
icon: FiXCircle,
title: 'Prohibited Activities',
content: [
{
subtitle: 'System Interference',
text: 'You may not attempt to gain unauthorized access to our systems,interfere with our services,or use automated tools to access our platform in ways that could damage or impair our services.'
},
{
subtitle: 'Harmful Content',
text: 'You may not use our services to create,store,or transmit content that is illegal,harmful,threatening,abusive,defamatory,or otherwise objectionable.'
},
{
subtitle: 'Reverse Engineering',
text: 'You may not reverse engineer,decompile,disassemble,or attempt to derive the source code of our software or services,except as permitted by applicable law.'
}
]
},
{
icon: FiAlertTriangle,
title: 'Disclaimers and Limitations',
content: [
{
subtitle: 'Service Disclaimer',
text: 'Our services are provided "as is" and "as available" without warranties of any kind,either express or implied. We do not warrant that our services will be uninterrupted,error-free,or completely secure.'
},
{
subtitle: 'AI Accuracy',
text: 'While we strive for accuracy in our AI solutions,we cannot guarantee that all outputs will be error-free or suitable for every purpose. Users should validate AI-generated results before making business decisions.'
},
{
subtitle: 'Limitation of Liability',
text: 'To the maximum extent permitted by law,Reliabotix shall not be liable for any indirect,incidental,special,consequential,or punitive damages arising from your use of our services.'
}
]
}
];

return (
<div className="relative">
{/* Animated Background */}
<div className="absolute inset-0 overflow-hidden pointer-events-none">
<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
</div>

{/* Header */}
<section ref={headerRef} className="py-24 lg:py-32 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<motion.div
initial={{opacity: 0,y: 20}}
animate={headerInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6}}
className="text-center"
>
<h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8">
<span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
Terms of Use
</span>
</h1>
<p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
These Terms of Use govern your access to and use of Reliabotix AI Solutions services. Please read these terms carefully before using our platform.
</p>
<div className="inline-flex items-center glass-card px-6 py-3 rounded-xl">
<SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-400 mr-3" />
<span className="text-gray-300">Last updated: January 15,2025</span>
</div>
</motion.div>
</div>
</section>

{/* Terms Content */}
<section ref={contentRef} className="py-20 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="space-y-16">
{sections.map((section,index)=> (
<motion.div
key={index}
initial={{opacity: 0,y: 20}}
animate={contentInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6,delay: index * 0.1}}
className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-dark-700/30"
>
<div className="flex items-center mb-8">
<div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-purple rounded-2xl mr-6 shadow-lg">
<SafeIcon icon={section.icon} className="w-8 h-8 text-white" />
</div>
<h2 className="text-3xl lg:text-4xl font-bold text-white">{section.title}</h2>
</div>

<div className="space-y-8">
{section.content.map((item,itemIndex)=> (
<div key={itemIndex}>
<h3 className="text-xl font-semibold text-primary-300 mb-4">{item.subtitle}</h3>
<p className="text-gray-300 leading-relaxed">{item.text}</p>
</div>
))}
</div>
</motion.div>
))}
</div>
</div>
</section>

{/* Payment and Billing */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6 flex items-center">
<SafeIcon icon={FiFileText} className="w-6 h-6 text-primary-400 mr-3" />
Payment and Billing Terms
</h2>
<div className="space-y-4 text-gray-300">
<p><strong>Payment Terms:</strong> Payment is due according to the billing schedule specified in your service agreement. All fees are non-refundable unless otherwise specified.</p>
<p><strong>Price Changes:</strong> We reserve the right to change our pricing with 30 days' notice to existing customers. New pricing will apply to subsequent billing cycles.</p>
<p><strong>Suspension:</strong> We may suspend your access to our services if payment is overdue by more than 30 days,with prior notice.</p>
<p><strong>Taxes:</strong> You are responsible for all applicable taxes related to your use of our services,except for taxes based on Reliabotix's net income.</p>
</div>
</div>
</div>
</section>

{/* Termination */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6 flex items-center">
<SafeIcon icon={FiXCircle} className="w-6 h-6 text-primary-400 mr-3" />
Termination
</h2>
<div className="space-y-4 text-gray-300">
<p><strong>Termination by You:</strong> You may terminate your account at any time by contacting us or through your account settings. Termination will be effective at the end of your current billing period.</p>
<p><strong>Termination by Us:</strong> We may terminate or suspend your access to our services immediately if you violate these terms,engage in fraudulent activity,or for other legitimate business reasons.</p>
<p><strong>Effect of Termination:</strong> Upon termination,your right to use our services will cease immediately. We will retain your data for a reasonable period to allow for account reactivation,after which it will be deleted.</p>
<p><strong>Survival:</strong> Provisions regarding intellectual property,disclaimers,limitations of liability,and dispute resolution will survive termination.</p>
</div>
</div>
</div>
</section>

{/* Governing Law */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6 flex items-center">
<SafeIcon icon={FiShield} className="w-6 h-6 text-primary-400 mr-3" />
Governing Law and Disputes
</h2>
<div className="space-y-4 text-gray-300">
<p><strong>Governing Law:</strong> These terms are governed by the laws of the jurisdiction where Reliabotix is incorporated,without regard to conflict of law principles.</p>
<p><strong>Dispute Resolution:</strong> Any disputes arising from these terms or your use of our services will be resolved through binding arbitration,except for claims that may be brought in small claims court.</p>
<p><strong>Class Action Waiver:</strong> You agree not to participate in class action lawsuits or class-wide arbitration against Reliabotix.</p>
<p><strong>Injunctive Relief:</strong> We may seek injunctive relief in any court of competent jurisdiction to protect our intellectual property or confidential information.</p>
</div>
</div>
</div>
</section>

{/* Contact Information */}
<section className="py-20 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-gradient-to-r from-primary-500/10 to-accent-purple/10 backdrop-blur-sm rounded-3xl p-8 border border-primary-500/20 text-center">
<h2 className="text-2xl font-bold text-primary-300 mb-6 flex items-center justify-center">
<SafeIcon icon={FiMail} className="w-6 h-6 mr-3" />
Questions About These Terms?
</h2>
<p className="text-primary-200 mb-6 max-w-2xl mx-auto">
If you have any questions about these Terms of Use or need clarification on any provisions,please contact our legal team.
</p>
<div className="space-y-2 text-primary-200 mb-6">
<p><strong>Email:</strong> info@reliabotix.com</p>
<p><strong>Address:</strong> Reliabotix AI Solutions,Legal Department</p>
<p><strong>Phone:</strong> +1 (555) 123-4567</p>
</div>
<motion.div
whileHover={{scale: 1.05}}
whileTap={{scale: 0.95}}
>
<a
href="/#/contact"
className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300"
>
Contact Legal Team
</a>
</motion.div>
</div>
</div>
</section>
</div>
);
};

export default Terms;